import classNames from "classnames";
import React, { FC, useRef } from "react";
import {
  LuAlertTriangle,
  LuAlignCenter,
  LuAlignLeft,
  LuAlignRight,
  LuMove,
  LuZoomIn,
  LuZoomOut,
} from "react-icons/lu";

import { BILLING_SECONDARY_BUTTON } from "../../../billing/ui/BillingUi";
import {
  Align,
  alignPlacement,
  currentAlign,
  fillZoom,
  FRAME,
  MAX_ZOOM,
  MIN_ZOOM,
  movePlacement,
  pictureNotes,
  Placement,
  printQuality,
  Rect,
  zoomPlacement,
} from "./logo.utils";
import { LoadedLogo } from "./logoCanvas";
import { CHECKERBOARD, FramedPicture } from "./LogoFrame";

export const cropOf = (logo: LoadedLogo, trimOn: boolean): Rect =>
  trimOn && logo.trim
    ? logo.trim
    : { x: 0, y: 0, width: logo.width, height: logo.height };

export const QualityChip: FC<{
  level: "sharp" | "good" | "blurry";
  label: string;
}> = ({ level, label }) => (
  <span
    className={classNames(
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold",
      level === "sharp" && "bg-[#E7F7EE] text-[#15803D]",
      level === "good" && "bg-[#EEF2FF] text-[#4F46E5]",
      level === "blurry" && "bg-[#FFF4E5] text-[#B45309]",
    )}>
    <span
      className={classNames(
        "h-1.5 w-1.5 rounded-full",
        level === "sharp" && "bg-[#16A34A]",
        level === "good" && "bg-[#6B4EFF]",
        level === "blurry" && "bg-[#F59E0B]",
      )}
    />
    {label}
  </span>
);

type Props = {
  logo: LoadedLogo;
  trimOn: boolean;
  placement: Placement;
  onTrimChange: (on: boolean) => void;
  onPlacementChange: (placement: Placement) => void;
};

const ALIGNS: { align: Align; label: string; icon: JSX.Element }[] = [
  { align: "left", label: "Left", icon: <LuAlignLeft /> },
  { align: "center", label: "Center", icon: <LuAlignCenter /> },
  { align: "right", label: "Right", icon: <LuAlignRight /> },
];

export const LogoEditor: FC<Props> = ({
  logo,
  trimOn,
  placement,
  onTrimChange,
  onPlacementChange,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; start: Placement } | null>(null);
  const crop = cropOf(logo, trimOn);
  const quality = printQuality(crop, placement.zoom);
  const notes = pictureNotes({
    width: crop.width,
    height: crop.height,
    trimmed: trimOn && !!logo.trim,
    mostlyWhite: logo.mostlyWhite,
    quality,
    isSvg: logo.isSvg,
  });
  const align = currentAlign(crop, placement);

  const toFrameUnits = () =>
    FRAME.width / (frameRef.current?.clientWidth || FRAME.width);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, start: placement };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const k = toFrameUnits();
    onPlacementChange(
      movePlacement(
        crop,
        drag.current.start,
        (e.clientX - drag.current.x) * k,
        (e.clientY - drag.current.y) * k,
      ),
    );
  };
  const endDrag = () => {
    drag.current = null;
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 60 : 12;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    onPlacementChange(movePlacement(crop, placement, move[0], move[1]));
  };

  const zoomPercent = Math.round(placement.zoom * 100);
  const setZoom = (zoom: number) =>
    onPlacementChange(zoomPlacement(crop, placement, zoom));

  return (
    <div className={"flex flex-col gap-4"}>
      <div>
        <div
          ref={frameRef}
          role={"application"}
          aria-label={
            "Logo position. Drag the logo or use the arrow keys to move it."
          }
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
          className={
            "outline relative aspect-[3/1] w-full cursor-move touch-none overflow-hidden rounded-xl outline-2 outline-offset-2 outline-[#6B4EFF] focus-visible:outline-[3px]"
          }
          style={CHECKERBOARD}
          data-testid={"logo-frame"}>
          <FramedPicture
            url={logo.url}
            width={logo.width}
            height={logo.height}
            crop={crop}
            placement={placement}
          />
        </div>
        <div
          className={
            "mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-[#6B7280]"
          }>
          <span className={"inline-flex items-center gap-1.5"}>
            <LuMove className={"text-[13px]"} /> Drag the logo to move it. The
            purple box is the logo space on the invoice.
          </span>
          <span>Checkered = transparent (prints as white paper)</span>
        </div>
      </div>

      <div className={"grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]"}>
        <div>
          <div className={"mb-1.5 flex items-center justify-between"}>
            <label
              htmlFor={"logo-zoom"}
              className={"text-xs font-bold text-[#9097A8]"}>
              SIZE
            </label>
            <span className={"text-xs font-semibold text-[#3A4256]"}>
              {zoomPercent}%
            </span>
          </div>
          <div className={"flex items-center gap-2"}>
            <button
              type={"button"}
              aria-label={"Smaller"}
              onClick={() => setZoom(placement.zoom - 0.1)}
              className={
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#DFE2E8] text-[#3A4256] hover:bg-[#F7F8FA]"
              }>
              <LuZoomOut />
            </button>
            <input
              id={"logo-zoom"}
              type={"range"}
              min={MIN_ZOOM * 100}
              max={MAX_ZOOM * 100}
              step={1}
              value={zoomPercent}
              onChange={(e) => setZoom(Number(e.target.value) / 100)}
              className={"h-1.5 w-full cursor-pointer accent-[#6B4EFF]"}
            />
            <button
              type={"button"}
              aria-label={"Larger"}
              onClick={() => setZoom(placement.zoom + 0.1)}
              className={
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#DFE2E8] text-[#3A4256] hover:bg-[#F7F8FA]"
              }>
              <LuZoomIn />
            </button>
          </div>
          <div className={"mt-2 flex flex-wrap gap-2"}>
            <button
              type={"button"}
              className={classNames(
                BILLING_SECONDARY_BUTTON,
                "!h-8 !px-3 !text-xs",
              )}
              onClick={() =>
                onPlacementChange(
                  alignPlacement(
                    crop,
                    { ...placement, zoom: 1 },
                    align || "left",
                  ),
                )
              }>
              Fit in the space
            </button>
            <button
              type={"button"}
              className={classNames(
                BILLING_SECONDARY_BUTTON,
                "!h-8 !px-3 !text-xs",
              )}
              onClick={() =>
                onPlacementChange(
                  alignPlacement(
                    crop,
                    { ...placement, zoom: fillZoom(crop) },
                    "center",
                  ),
                )
              }>
              Fill the space
            </button>
          </div>
        </div>
        <div>
          <div className={"mb-1.5 text-xs font-bold text-[#9097A8]"}>
            POSITION
          </div>
          <div
            className={
              "inline-flex rounded-[10px] border border-[#DFE2E8] bg-white p-0.5"
            }>
            {ALIGNS.map((item) => (
              <button
                key={item.align}
                type={"button"}
                aria-pressed={align === item.align}
                onClick={() =>
                  onPlacementChange(alignPlacement(crop, placement, item.align))
                }
                className={classNames(
                  "inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold",
                  align === item.align
                    ? "bg-[#6B4EFF] text-white"
                    : "text-[#3A4256] hover:bg-[#F7F8FA]",
                )}>
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className={
          "flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#F7F8FA] px-4 py-3"
        }>
        <label
          className={
            "inline-flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#1F2433]"
          }>
          <input
            type={"checkbox"}
            className={"h-4 w-4 accent-[#6B4EFF]"}
            checked={trimOn && !!logo.trim}
            disabled={!logo.trim}
            onChange={(e) => onTrimChange(e.target.checked)}
          />
          {logo.trim
            ? "Cut off the empty space around the logo"
            : "No empty space around the logo to cut off"}
        </label>
        <div className={"flex items-center gap-2 text-xs text-[#6B7280]"}>
          <span>
            {logo.isSvg
              ? "SVG (sharp at any size)"
              : `${crop.width} × ${crop.height} px`}
          </span>
          <QualityChip
            level={logo.isSvg ? "sharp" : quality.level}
            label={logo.isSvg ? "Sharp when printed" : quality.label}
          />
        </div>
      </div>

      {notes.length > 0 && (
        <div className={"flex flex-col gap-2"}>
          {notes.map((note) => (
            <div
              key={note}
              className={
                "flex gap-2 rounded-xl border border-[#FDE7C2] bg-[#FFF8EC] px-3.5 py-2.5 text-[13px] leading-5 text-[#7C4A03]"
              }>
              <LuAlertTriangle className={"mt-0.5 shrink-0 text-[#D97706]"} />
              <span>{note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
